import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { api } from '../api';
import NavIcon from '../components/NavIcon';

// ── Column specs for each sheet ────────────────────────────────────────────────
const SHEET_SPECS = {
  Categories: {
    label: 'Categories',
    icon: 'categories',
    columns: ['Name*', 'Description'],
    example: [
      ['Name*', 'Description'],
      ['Beverages', 'Drinks and liquids'],
      ['Snacks', 'Packaged snack items'],
    ],
    desc: 'One row per category. Name is required.',
  },
  Suppliers: {
    label: 'Suppliers',
    icon: 'suppliers',
    columns: ['Name*', 'Phone', 'Address'],
    example: [
      ['Name*', 'Phone', 'Address'],
      ['ABC Traders', '9800000001', 'Kathmandu'],
      ['XYZ Supplies', '', 'Pokhara'],
    ],
    desc: 'One row per supplier. Name is required.',
  },
  Products: {
    label: 'Products',
    icon: 'products',
    columns: ['Name*', 'CategoryName*', 'SalePrice*', 'CostPrice*', 'Stock', 'Unit'],
    example: [
      ['Name*', 'CategoryName*', 'SalePrice*', 'CostPrice*', 'Stock', 'Unit'],
      ['Coca Cola 500ml', 'Beverages', 60, 45, 100, 'pcs'],
      ['Lays Classic', 'Snacks', 30, 22, 200, 'pcs'],
    ],
    desc: 'CategoryName must match a category in the Categories sheet or already exist. Unit defaults to "pcs" if blank.',
  },
  Sales: {
    label: 'Sales',
    icon: 'sales',
    columns: ['Date*', 'ProductName*', 'Quantity*', 'UnitPrice*', 'Note'],
    example: [
      ['Date*', 'ProductName*', 'Quantity*', 'UnitPrice*', 'Note'],
      ['2024-01-15', 'Coca Cola 500ml', 5, 60, 'Walk-in customer'],
      ['2024-01-15', 'Lays Classic', 3, 30, 'Walk-in customer'],
      ['2024-01-16', 'Coca Cola 500ml', 2, 60, ''],
    ],
    desc: 'Multiple rows with the same Date+Note are grouped into one sale. ProductName must match an existing product.',
  },
  Purchases: {
    label: 'Purchases',
    icon: 'purchases',
    columns: ['Date*', 'ProductName*', 'Quantity*', 'UnitCost*', 'SupplierName', 'Note'],
    example: [
      ['Date*', 'ProductName*', 'Quantity*', 'UnitCost*', 'SupplierName', 'Note'],
      ['2024-01-10', 'Coca Cola 500ml', 50, 43, 'ABC Traders', 'Monthly restock'],
      ['2024-01-10', 'Lays Classic', 100, 20, 'ABC Traders', 'Monthly restock'],
    ],
    desc: 'Multiple rows with the same Date+SupplierName+Note are grouped into one purchase.',
  },
};

// ── Excel download helper ──────────────────────────────────────────────────────
function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  for (const [sheetName, spec] of Object.entries(SHEET_SPECS)) {
    const ws = XLSX.utils.aoa_to_sheet(spec.example);
    // Style header row bold by setting cell metadata
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[addr]) continue;
      ws[addr].s = { font: { bold: true }, fill: { fgColor: { rgb: 'D9E1F2' } } };
    }
    // Set column widths
    ws['!cols'] = spec.columns.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  }
  XLSX.writeFile(wb, 'storebook_import_template.xlsx');
  toast.success('Template downloaded!');
}

// ── Parse uploaded Excel into structured JSON ──────────────────────────────────
function parseExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
        const result = {};
        for (const sheetName of wb.SheetNames) {
          const ws = wb.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
          result[sheetName] = rows;
        }
        resolve(result);
      } catch (err) {
        reject(new Error('Could not read Excel file: ' + err.message));
      }
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsArrayBuffer(file);
  });
}

// ── Transform flat Excel rows → API request payload ────────────────────────────
function buildPayload(sheets) {
  const warnings = [];

  // Categories
  const categories = (sheets['Categories'] || [])
    .filter(r => r['Name*'])
    .map(r => ({ name: String(r['Name*']).trim(), description: r['Description'] ? String(r['Description']).trim() : null }));

  // Suppliers
  const suppliers = (sheets['Suppliers'] || [])
    .filter(r => r['Name*'])
    .map(r => ({
      name: String(r['Name*']).trim(),
      phone: r['Phone'] ? String(r['Phone']).trim() : null,
      address: r['Address'] ? String(r['Address']).trim() : null,
    }));

  // Products
  const products = (sheets['Products'] || [])
    .filter(r => r['Name*'])
    .map(r => ({
      name: String(r['Name*']).trim(),
      categoryName: String(r['CategoryName*'] || '').trim(),
      salePrice: parseFloat(r['SalePrice*']) || 0,
      costPrice: parseFloat(r['CostPrice*']) || 0,
      stock: parseFloat(r['Stock']) || 0,
      unit: r['Unit'] ? String(r['Unit']).trim() : 'pcs',
    }));

  // Sales — group rows by Date+Note into sale objects
  const salesMap = new Map();
  for (const r of (sheets['Sales'] || [])) {
    if (!r['Date*'] || !r['ProductName*']) continue;
    const dateStr = r['Date*'] instanceof Date
      ? r['Date*'].toISOString().split('T')[0]
      : String(r['Date*']).trim();
    const note = r['Note'] ? String(r['Note']).trim() : '';
    const key = `${dateStr}||${note}`;
    if (!salesMap.has(key)) salesMap.set(key, { date: dateStr, note: note || null, items: [] });
    salesMap.get(key).items.push({
      productName: String(r['ProductName*']).trim(),
      quantity: parseFloat(r['Quantity*']) || 0,
      unitPrice: parseFloat(r['UnitPrice*']) || 0,
    });
  }
  const sales = [...salesMap.values()];

  // Purchases — group rows by Date+SupplierName+Note
  const purchMap = new Map();
  for (const r of (sheets['Purchases'] || [])) {
    if (!r['Date*'] || !r['ProductName*']) continue;
    const dateStr = r['Date*'] instanceof Date
      ? r['Date*'].toISOString().split('T')[0]
      : String(r['Date*']).trim();
    const supplier = r['SupplierName'] ? String(r['SupplierName']).trim() : '';
    const note = r['Note'] ? String(r['Note']).trim() : '';
    const key = `${dateStr}||${supplier}||${note}`;
    if (!purchMap.has(key)) purchMap.set(key, { date: dateStr, supplierName: supplier || null, note: note || null, items: [] });
    purchMap.get(key).items.push({
      productName: String(r['ProductName*']).trim(),
      quantity: parseFloat(r['Quantity*']) || 0,
      unitCost: parseFloat(r['UnitCost*']) || 0,
    });
  }
  const purchases = [...purchMap.values()];

  return { categories, suppliers, products, sales, purchases, warnings };
}

// ── Preview table ──────────────────────────────────────────────────────────────
function PreviewTable({ spec, rows }) {
  if (!rows || rows.length === 0) return (
    <div style={{ color: 'var(--muted)', fontSize: 12, padding: '8px 0' }}>No data in this sheet</div>
  );
  const headers = Object.keys(rows[0]);
  return (
    <div style={{ overflowX: 'auto', maxHeight: 200, border: '1px solid var(--border)', borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--surface2)', position: 'sticky', top: 0 }}>
            {headers.map(h => (
              <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              {headers.map(h => (
                <td key={h} style={{ padding: '5px 10px', color: 'var(--text)' }}>
                  {row[h] instanceof Date ? row[h].toLocaleDateString() : String(row[h] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 50 && (
        <div style={{ padding: '6px 10px', color: 'var(--muted)', fontSize: 11 }}>
          … and {rows.length - 50} more rows
        </div>
      )}
    </div>
  );
}

// ── Result summary ─────────────────────────────────────────────────────────────
function ResultSummary({ result }) {
  const counts = [
    { label: 'Categories', value: result.categoriesImported, icon: 'categories' },
    { label: 'Suppliers', value: result.suppliersImported, icon: 'suppliers' },
    { label: 'Products', value: result.productsImported, icon: 'products' },
    { label: 'Sales', value: result.salesImported, icon: 'sales' },
    { label: 'Purchases', value: result.purchasesImported, icon: 'purchases' },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10, marginBottom: 16 }}>
        {counts.map(c => (
          <div key={c.label} style={{
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '12px 8px', textAlign: 'center'
          }}>
            <div style={{ fontSize: 20, display: 'flex', justifyContent: 'center' }}><NavIcon name={c.icon} size={20} /></div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.value > 0 ? 'var(--accent2)' : 'var(--muted)' }}>{c.value}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {result.errors.length > 0 && (
        <div style={{ background: 'rgba(196,85,61,0.08)', border: '1px solid var(--danger)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 600, color: 'var(--danger)', marginBottom: 6, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><NavIcon name="errorCircle" size={14} /> Errors ({result.errors.length})</div>
          {result.errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 3 }}>• {e}</div>)}
        </div>
      )}

      {result.warnings.length > 0 && (
        <div style={{ background: 'rgba(212,162,76,0.08)', border: '1px solid var(--warn)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontWeight: 600, color: 'var(--warn)', marginBottom: 6, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><NavIcon name="warning" size={14} /> Warnings ({result.warnings.length})</div>
          {result.warnings.map((w, i) => <div key={i} style={{ fontSize: 12, color: 'var(--warn)', marginBottom: 3 }}>• {w}</div>)}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function BulkImport() {
  const [step, setStep] = useState('idle'); // idle | preview | importing | done
  const [sheets, setSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState('Categories');
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    try {
      const parsed = await parseExcel(file);
      setSheets(parsed);
      setFileName(file.name);
      setActiveSheet(Object.keys(parsed)[0] || 'Categories');
      setStep('preview');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleImport = async () => {
    const { categories, suppliers, products, sales, purchases } = buildPayload(sheets);
    const total = categories.length + suppliers.length + products.length + sales.length + purchases.length;
    if (total === 0) { toast.error('No valid rows found in the file.'); return; }

    setStep('importing');
    try {
      const res = await api.bulkImport({ categories, suppliers, products, sales, purchases });
      setResult(res);
      setStep('done');
      const imported = res.categoriesImported + res.suppliersImported + res.productsImported + res.salesImported + res.purchasesImported;
      if (imported > 0) toast.success(`Import complete — ${imported} records added`);
      else toast('Nothing new was imported', { icon: 'ℹ️' });
    } catch (err) {
      toast.error('Import failed: ' + err.message);
      setStep('preview');
    }
  };

  const reset = () => {
    setStep('idle');
    setSheets(null);
    setResult(null);
    setFileName('');
    if (inputRef.current) inputRef.current.value = '';
  };

  // ── Idle / drop zone ──────────────────────────────────────────────────────
  if (step === 'idle') return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Bulk Import</h2>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2 }}>
            Import categories, suppliers, products, sales & purchases from an Excel workbook
          </p>
        </div>
        <button className="btn btn-secondary" onClick={downloadTemplate}>
          ⬇ Download Template
        </button>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 14,
          padding: '56px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? 'rgba(204,122,61,0.06)' : 'var(--surface)',
          transition: 'all 0.2s',
          marginBottom: 32,
        }}
      >
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><NavIcon name="folderOpen" size={40} /></div>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
          Drop your Excel file here
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>
          or click to browse — supports .xlsx and .xls
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])}
        />
      </div>

      {/* Sheet reference cards */}
      <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, color: 'var(--muted)' }}>
        EXPECTED SHEETS & COLUMNS
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {Object.entries(SHEET_SPECS).map(([key, spec]) => (
          <div key={key} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 18 }}>{spec.icon}</span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{spec.label}</span>
              <span style={{ marginLeft: 'auto', background: 'var(--surface2)', color: 'var(--muted)', borderRadius: 6, padding: '2px 8px', fontSize: 11 }}>
                Sheet: {key}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{spec.desc}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {spec.columns.map(col => (
                <span key={col} style={{
                  background: col.endsWith('*') ? 'rgba(204,122,61,0.12)' : 'var(--surface2)',
                  color: col.endsWith('*') ? 'var(--accent)' : 'var(--muted)',
                  borderRadius: 5, padding: '2px 7px', fontSize: 11, fontFamily: 'var(--mono)'
                }}>{col}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Preview ───────────────────────────────────────────────────────────────
  if (step === 'preview') {
    const sheetNames = Object.keys(sheets);
    const { categories, suppliers, products, sales, purchases } = buildPayload(sheets);
    const counts = { Categories: categories.length, Suppliers: suppliers.length, Products: products.length, Sales: sales.length, Purchases: purchases.length };

    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h2>Preview Import</h2>
            <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}><NavIcon name="document" size={13} /> {fileName}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={reset}>← Back</button>
            <button className="btn btn-primary" onClick={handleImport}>
              Import Data
            </button>
          </div>
        </div>

        {/* Count summary */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {Object.entries(SHEET_SPECS).map(([key, spec]) => (
            <div key={key} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 7
            }}>
              <span>{spec.icon}</span>
              <span style={{ fontWeight: 600, color: (counts[key] || 0) > 0 ? 'var(--text)' : 'var(--muted)' }}>
                {counts[key] ?? 0}
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>{spec.label}</span>
            </div>
          ))}
        </div>

        {/* Sheet tabs + preview */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
            {sheetNames.map(name => (
              <button
                key={name}
                onClick={() => setActiveSheet(name)}
                style={{
                  padding: '10px 18px',
                  background: activeSheet === name ? 'var(--surface2)' : 'transparent',
                  border: 'none',
                  borderBottom: activeSheet === name ? '2px solid var(--accent)' : '2px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeSheet === name ? 600 : 400,
                  color: activeSheet === name ? 'var(--text)' : 'var(--muted)',
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                <NavIcon name={SHEET_SPECS[name]?.icon ?? 'clipboard'} size={14} /> {name}
                {(sheets[name]?.length ?? 0) > 0 && (
                  <span style={{ background: 'var(--accent)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10 }}>
                    {sheets[name].length}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div style={{ padding: 16 }}>
            {SHEET_SPECS[activeSheet] && (
              <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
                ℹ️ {SHEET_SPECS[activeSheet].desc}
              </p>
            )}
            <PreviewTable spec={SHEET_SPECS[activeSheet]} rows={sheets[activeSheet]} />
          </div>
        </div>
      </div>
    );
  }

  // ── Importing ─────────────────────────────────────────────────────────────
  if (step === 'importing') return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Importing data…</div>
      <div style={{ color: 'var(--muted)', fontSize: 13 }}>Please wait while we process your file.</div>
    </div>
  );

  // ── Done ──────────────────────────────────────────────────────────────────
  if (step === 'done' && result) return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Import Complete</h2>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}><NavIcon name="document" size={13} /> {fileName}</p>
        </div>
        <button className="btn btn-primary" onClick={reset}>↩ Import Another File</button>
      </div>
      <ResultSummary result={result} />
    </div>
  );

  return null;
}
